import React, { lazy, Suspense } from 'react'

import type { DynamicLoader, DynamicOptions } from './types'

/**
 * Public surface of the navigation shim: pages import their navigation
 * primitives from `@/lib/navigation/*` and nowhere else (apps/app/CLAUDE.md),
 * so these type names are part of that contract.
 *
 * @public
 */
export type { DynamicLoader, DynamicLoadingProps, DynamicOptions } from './types'

/**
 * next/dynamic over React.lazy + Suspense — which is exactly what the
 * installed Next does (`next/dist/shared/lib/lazy-dynamic/loadable.js`): no
 * delay before the fallback, no error boundary of its own. `ssr` is
 * meaningless in a client-only app and ignored. The loading component gets
 * Next's LoadingComponentProps shape with Next's own values.
 */
export default function dynamic<P>(loader: DynamicLoader<P>, options: DynamicOptions = {}): React.ComponentType<P> {
  const Lazy = lazy(async () => {
    const loaded = await loader()
    const component = (
      typeof loaded === 'object' && loaded !== null && 'default' in loaded ? loaded.default : loaded
    ) as React.ComponentType<P>
    return { default: component }
  })
  const LoadingComponent = options.loading
  const fallback = LoadingComponent ? <LoadingComponent isLoading pastDelay error={null} /> : null
  function DynamicComponent(props: P) {
    return (
      <Suspense fallback={fallback}>
        <Lazy {...(props as P & React.JSX.IntrinsicAttributes)} />
      </Suspense>
    )
  }
  DynamicComponent.displayName = 'Dynamic'
  return DynamicComponent
}
